import { useCallback } from 'react';
import { ISiteVisit, ISiteVisitPosition } from '../../types';

interface UseSiteVisitNavigationProps {
  siteVisit: ISiteVisit | null;
  position: ISiteVisitPosition;
  setPosition: (position: ISiteVisitPosition) => void;
  saveSiteVisitToServer: () => Promise<void>;
}

export const useSiteVisitNavigation = ({
  siteVisit,
  position,
  setPosition,
  saveSiteVisitToServer,
}: UseSiteVisitNavigationProps) => {
  const isFirstQuestion = useCallback((): boolean => {
    return position.categoryIndex === 0 && position.questionIndex === 0;
  }, [position]);

  const isLastQuestion = useCallback((): boolean => {
    if (!siteVisit) return true;
    const lastCategoryIndex = siteVisit.categories.length - 1;
    if (position.categoryIndex < lastCategoryIndex) return false;
    const lastCategory = siteVisit.categories[lastCategoryIndex];
    return position.questionIndex === lastCategory.questions.length - 1;
  }, [siteVisit, position]);

  const nextQuestion = useCallback(async (): Promise<boolean> => {
    if (!siteVisit || isLastQuestion()) return false;

    try {
      await saveSiteVisitToServer();
    } catch (err) {
      console.error('Failed to save before moving to next question:', err);
      return false;
    }

    const currentCategory = siteVisit.categories[position.categoryIndex];

    if (position.questionIndex < currentCategory.questions.length - 1) {
      setPosition({
        ...position,
        questionIndex: position.questionIndex + 1,
      });
      return true;
    }

    if (position.categoryIndex < siteVisit.categories.length - 1) {
      setPosition({
        categoryIndex: position.categoryIndex + 1,
        questionIndex: 0,
      });
      return true;
    }

    return false;
  }, [siteVisit, position, isLastQuestion, saveSiteVisitToServer, setPosition]);

  const previousQuestion = useCallback((): boolean => {
    if (!siteVisit || isFirstQuestion()) return false;

    if (position.questionIndex > 0) {
      setPosition({
        ...position,
        questionIndex: position.questionIndex - 1,
      });
      return true;
    }

    if (position.categoryIndex > 0) {
      const prevCategoryIndex = position.categoryIndex - 1;
      const prevCategory = siteVisit.categories[prevCategoryIndex];
      setPosition({
        categoryIndex: prevCategoryIndex,
        questionIndex: prevCategory.questions.length - 1,
      });
      return true;
    }

    return false;
  }, [siteVisit, position, isFirstQuestion, setPosition]);

  const goToQuestion = useCallback(
    (categoryIndex: number, questionIndex: number): boolean => {
      if (!siteVisit) return false;

      if (
        categoryIndex < 0 ||
        categoryIndex >= siteVisit.categories.length ||
        questionIndex < 0 ||
        questionIndex >= siteVisit.categories[categoryIndex].questions.length
      ) {
        return false;
      }

      setPosition({ categoryIndex, questionIndex });
      return true;
    },
    [siteVisit, setPosition]
  );

  const goToCategory = useCallback(
    (categoryIndex: number): boolean => {
      if (!siteVisit) return false;

      if (categoryIndex < 0 || categoryIndex >= siteVisit.categories.length) {
        return false;
      }

      setPosition({ categoryIndex, questionIndex: 0 });
      return true;
    },
    [siteVisit, setPosition]
  );

  return {
    isFirstQuestion,
    isLastQuestion,
    nextQuestion,
    previousQuestion,
    goToQuestion,
    goToCategory,
  };
};
